import { useSetAtom } from 'jotai';
import { type UIEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Group,
  Heading,
  Link,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Text,
  Tooltip,
} from '@optiaxiom/react';
import { IconCheckDouble, IconXmark } from '@optimizely/axiom-icons';

import { activeRightSidebarAtom } from '@frontend/atoms';
import { useInstanceId, useNamespacedNavigate } from '@frontend/hooks';

import {
  useAcknowledgeBadge,
  useCursorNotificationList,
  useNotificationActions,
  useNotificationFeatures,
  useUnreadCount,
} from '../hooks';
import type { GroupedNotification, Notification } from '../types';
import {
  formatTimestamp,
  formatUnreadCount,
  getDeepLinkPath,
  getNotificationVisuals,
} from '../utils';

import { notificationListItemStyles } from './NotificationTray.css';

const PAGE_SIZE = 20;
const MAX_TRAY_GROUPS = 40;

type NotificationTrayTab = 'all' | 'unread';

export function NotificationTray() {
  const [tab, setTab] = useState<NotificationTrayTab>('all');
  const setActiveRightSidebar = useSetAtom(activeRightSidebarAtom);
  const navigate = useNamespacedNavigate();
  const actions = useNotificationActions();
  const { badgeEnabled, groupingEnabled } = useNotificationFeatures();
  const { mutate: markSeen } = useAcknowledgeBadge();
  const { data: unreadCount } = useUnreadCount({ grouped: groupingEnabled });

  useEffect(() => {
    if (!badgeEnabled) return;
    markSeen();
    return () => markSeen();
  }, [markSeen, badgeEnabled]);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isPending } =
    useCursorNotificationList({
      limit: PAGE_SIZE,
      status: tab,
      grouped: groupingEnabled,
    });

  const groups = useMemo(() => data?.groups ?? [], [data?.groups]);
  const reachedTrayLimit = groups.length >= MAX_TRAY_GROUPS;
  const canFetchMore = Boolean(hasNextPage && !reachedTrayLimit && !isFetchingNextPage);

  const closeSidebar = useCallback(() => {
    setActiveRightSidebar(null);
  }, [setActiveRightSidebar]);

  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      if (!canFetchMore) return;
      const target = event.currentTarget;
      const distanceFromBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
      if (distanceFromBottom < 160) {
        void fetchNextPage();
      }
    },
    [canFetchMore, fetchNextPage]
  );

  const handleActivateSingle = useCallback(
    (notification: Notification) => {
      if (!notification.is_read) {
        actions.markAsRead.mutate(notification.id);
      }
      if (notification.type?.startsWith('messaging_')) {
        const meta = notification.metadata as Record<string, unknown> | undefined;
        document.dispatchEvent(
          new CustomEvent('opal::im::notificationClicked', {
            detail: {
              conversationId: meta?.conversation_id,
              messageId: meta?.message_id,
              msgThreadId: meta?.thread_id,
            },
            bubbles: true,
          })
        );
      }
      closeSidebar();
      navigate(getDeepLinkPath(notification));
    },
    [actions.markAsRead, closeSidebar, navigate]
  );

  const handleActivateGroup = useCallback(
    (group: GroupedNotification) => {
      if (group.count === 1 && group.notifications.length === 1) {
        handleActivateSingle(group.notifications[0]);
        return;
      }
      if (group.unread_count > 0) {
        actions.bulkMarkAsRead.mutate({ group_id: group.group_id });
      }
      closeSidebar();
      if (group.deep_link) {
        navigate(group.deep_link);
      } else {
        navigate(`/notifications?group_id=${group.group_id}`);
      }
    },
    [handleActivateSingle, actions.bulkMarkAsRead, closeSidebar, navigate]
  );

  const handleViewNotifications = useCallback(() => {
    closeSidebar();
    navigate('/notifications');
  }, [closeSidebar, navigate]);

  const handleMarkAllRead = useCallback(() => {
    actions.bulkMarkAsRead.mutate(undefined);
  }, [actions.bulkMarkAsRead]);

  const handleDelete = useCallback(
    (group: GroupedNotification) => {
      if (group.count === 1 && group.notifications.length === 1) {
        actions.deleteNotification.mutate(group.notifications[0].id);
      } else {
        actions.bulkDelete.mutate({ group_id: group.group_id });
      }
    },
    [actions.deleteNotification, actions.bulkDelete]
  );

  const body = (
    <NotificationTrayList
      emptyMessage={tab === 'unread' ? 'No unread notifications' : 'No notifications'}
      groups={groups}
      isFetchingNextPage={isFetchingNextPage}
      isPending={isPending}
      onActivate={handleActivateGroup}
      onDelete={handleDelete}
      onViewNotifications={handleViewNotifications}
    />
  );

  const unreadLabel = formatUnreadCount(unreadCount);

  return (
    <Group flexDirection="column" bg="bg.page" h="full">
      <Group alignItems="center" justifyContent="space-between" px="16" py="12" bg="bg.default">
        <Heading level="4" fontSize="lg" fontWeight="500">
          Notifications
        </Heading>

        <Tooltip content="Close notifications" delayDuration={0}>
          <Button
            appearance="subtle"
            aria-label="Close notifications"
            icon={<IconXmark />}
            onClick={closeSidebar}
            square
          />
        </Tooltip>
      </Group>

      <Tabs
        display="flex"
        flex="1"
        flexDirection="column"
        gap="8"
        h="full"
        onValueChange={value => setTab(value as NotificationTrayTab)}
        overflow="hidden"
        px="16"
        value={tab}
      >
        <TabsList alignItems="center" justifyContent="space-between" w="full">
          <Group>
            <TabsTrigger value="all" fontSize="sm" px="8">
              All
            </TabsTrigger>
            <TabsTrigger value="unread" fontSize="sm" px="8">
              {unreadLabel ? `Unread (${unreadLabel})` : 'Unread'}
            </TabsTrigger>
          </Group>
          <Button
            appearance="subtle"
            icon={<IconCheckDouble />}
            onClick={handleMarkAllRead}
            size="sm"
          >
            Mark all as read
          </Button>
        </TabsList>

        <TabsContent
          flex="1"
          onScroll={handleScroll}
          overflowY="auto"
          style={{ minHeight: 0 }}
          value="all"
        >
          {body}
        </TabsContent>
        <TabsContent
          flex="1"
          onScroll={handleScroll}
          overflowY="auto"
          style={{ minHeight: 0 }}
          value="unread"
        >
          {body}
        </TabsContent>
      </Tabs>
    </Group>
  );
}

type NotificationTrayListProps = {
  emptyMessage: string;
  groups: GroupedNotification[];
  isFetchingNextPage: boolean;
  isPending: boolean;
  onActivate: (group: GroupedNotification) => void;
  onDelete: (group: GroupedNotification) => void;
  onViewNotifications: () => void;
};

function NotificationTrayList({
  emptyMessage,
  groups,
  isFetchingNextPage,
  isPending,
  onActivate,
  onDelete,
  onViewNotifications,
}: NotificationTrayListProps) {
  const { instanceId } = useInstanceId();

  if (isPending) {
    return (
      <Group flexDirection="column" gap="0" p="16">
        {Array.from({ length: 5 }, (_, index) => (
          <Group flexDirection="column" gap="8" key={index} py="16">
            <Skeleton h="16" w="3/4" />
            <Skeleton h="16" w="full" />
            <Skeleton h="16" w="1/2" />
          </Group>
        ))}
      </Group>
    );
  }

  if (groups.length === 0) {
    return (
      <Group alignItems="center" justifyContent="center" p="32">
        <Text color="fg.tertiary">{emptyMessage}</Text>
      </Group>
    );
  }

  return (
    <Group flexDirection="column" gap="0">
      {groups.map(group => (
        <NotificationTrayItem
          group={group}
          key={group.group_id}
          onActivate={onActivate}
          onDelete={onDelete}
        />
      ))}
      {isFetchingNextPage && (
        <Group flexDirection="column" gap="8" p="16">
          <Skeleton h="16" w="3/4" />
          <Skeleton h="16" w="full" />
        </Group>
      )}
      <Group alignItems="center" justifyContent="center" p="16">
        <Link
          appearance="default"
          fontSize="sm"
          href={`/instances/${instanceId}/notifications`}
          onClick={onViewNotifications}
        >
          View all notifications
        </Link>
      </Group>
    </Group>
  );
}

type NotificationTrayItemProps = {
  group: GroupedNotification;
  onActivate: (group: GroupedNotification) => void;
  onDelete: (group: GroupedNotification) => void;
};

function NotificationTrayItem({ group, onActivate, onDelete }: NotificationTrayItemProps) {
  const [isHovered, setIsHovered] = useState(false);

  const isSingle = group.count === 1 && group.notifications.length === 1;
  const latestNotification = group.notifications[0];
  const isUnread = isSingle ? !latestNotification?.is_read : group.unread_count > 0;
  const displayTime = isSingle ? latestNotification?.created_at : group.latest_at;
  const timestamp = formatTimestamp(new Date(displayTime));
  const message = isSingle ? latestNotification?.message : `${group.count} notifications`;

  return (
    <Group
      alignItems="start"
      bg={isUnread ? 'bg.secondary' : 'bg.default'}
      borderB="1"
      borderColor="border.tertiary"
      className={notificationListItemStyles}
      cursor="pointer"
      gap="12"
      onClick={() => onActivate(group)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      p="16"
      transition="colors"
    >
      <NotificationTypeIcon type={group.type} />

      <Group flex="1" flexDirection="column" gap="8">
        <Group alignItems="start" gap="16" justifyContent="space-between">
          <Group alignItems="center" gap="8">
            <Text fontSize="md" fontWeight={isUnread ? '700' : '500'} lineClamp="2">
              {group.title}
            </Text>
            {!isSingle && <Badge intent="neutral">{group.count}</Badge>}
          </Group>
          <Group
            alignItems="center"
            flex="none"
            gap="8"
            justifyContent="end"
            style={{ minHeight: 28, minWidth: 64 }}
          >
            {isHovered ? (
              <Button
                appearance="subtle"
                aria-label={`Dismiss ${group.title}`}
                icon={<IconXmark />}
                onClick={e => {
                  e.stopPropagation();
                  onDelete(group);
                }}
                size="sm"
                square
              />
            ) : (
              <>
                <Text color="fg.tertiary" fontSize="xs" whiteSpace="nowrap">
                  {timestamp.time}
                </Text>
                {isUnread && (
                  <Box bg="bg.error" flex="none" rounded="full" style={{ width: 8, height: 8 }} />
                )}
              </>
            )}
          </Group>
        </Group>
        <Text color="fg.secondary" fontSize="sm" lineClamp="3">
          {message}
        </Text>
      </Group>
    </Group>
  );
}

function NotificationTypeIcon({ type }: { type: GroupedNotification['type'] }) {
  const visuals = getNotificationVisuals(type);
  if (!visuals) return null;
  return (
    <Box color={visuals.color} display="inline-flex" mt="2">
      <visuals.Icon />
    </Box>
  );
}
